/* eslint-disable max-lines */
import React from 'react';

import { Box, Flex, Link, Text } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { useAnalytics } from '@r-client/shared/data/analytics';
import { useAuth } from '@r-client/shared/data/auth';
import {
  E_US_ACCOUNT_ACCESS_STATE,
  useJurisdiction,
  useUsAccountAccessState,
} from '@r-client/shared/feature/global-auth';
import { SvgComponent, SvgIconLight } from '@r-client/shared/ui/icons';
import { BasicMenu, BasicMenuItem } from '@r-client/shared/ui/menu';
import { useHasWallet } from '@r-client/wallet/feature/session';

import { EditableEntities } from '../../editable-entities/editable-entities';
import { userNavItems } from '../../navigation-data';
import { TEditableEntities } from '../../types';
import {
  getUserTeasers,
  teasersToEditableEntities,
} from '../../utilities/teasers-to-editable-entities';

import styles from '../mobile-user-navigation.module.scss';

export interface IUserMobileNavRowContentProps {
  userSlug: string;
  editableOfferings?: TEditableEntities;
  onNotificationsClick?: VoidFunction;
  notificationsCount: number;
  messagesCount: number;
}

function ListItemRightSide({
  notificationsCount,
  messagesCount,
  title,
  icon,
}: {
  notificationsCount: number;
  messagesCount: number;
  title: string;
  icon: SvgComponent;
}) {
  switch (true) {
    case title === 'Notifications' && !!notificationsCount:
      return (
        <Box className={styles.notificationsCount}>{notificationsCount}</Box>
      );
    case title === 'Messages' && !!messagesCount:
      return <Box className={styles.notificationsCount}>{messagesCount}</Box>;
    default:
      return <SvgIconLight icon={icon} width={22} height={22} />;
  }
}

export const UserMobileNavRowContent = observer(
  function UserMobileNavRowContent({
    userSlug,
    editableOfferings,
    onNotificationsClick,
    notificationsCount,
    messagesCount,
  }: IUserMobileNavRowContentProps) {
    const { viewer, signOut } = useAuth();
    const teasers = teasersToEditableEntities(getUserTeasers(viewer));

    const { hasWallet } = useHasWallet();
    const analytics = useAnalytics();
    const jurisdiction = useJurisdiction();

    const allItems = userNavItems({
      userSlug,
      mobile: true,
      hasWallet,
      hasAllowedOktaDomain: viewer?.info?.hasAllowedOktaDomain,
    }).filter(Boolean);

    let logoutItem: (typeof allItems)[number] | undefined;
    const regularItems = allItems.filter((item) => {
      if (item?.href === '/logout') {
        logoutItem = item;
        return false;
      }
      return true;
    });

    const hasAccountInEU = jurisdiction.accounts.hasEuAccount;
    const hasAccountInUK = jurisdiction.accounts.hasUkAccount;
    const hasAccountInUS = jurisdiction.accounts.hasUsAccount;

    const {
      state: accessState,
      isUsAuthenticated: showAuthenticatedMenu,
      hasCrossRegionAccountAccess,
    } = useUsAccountAccessState(!!viewer?.info);

    const handleLogout = (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
      e.preventDefault();
      signOut({ redirectTo: '/logout' });
    };

    const handleItemClick = (title: string) => {
      switch (true) {
        case title === 'Notifications':
          return onNotificationsClick?.();
        default:
          return undefined;
      }
    };

    const trackNavigation = (
      title = '',
      trackingParams?: Record<string, string>
    ) => {
      analytics.track({
        name: 'user_menu_clicked',
        params: {
          label: `user_menu_${title.toLowerCase().replace(' ', '_')}`,
          ...trackingParams,
        },
      });
    };

    return (
      <Box flex="1">
        <BasicMenu>
          {accessState === E_US_ACCOUNT_ACCESS_STATE.CAN_ONBOARD && (
            <>
              <BasicMenuItem>
                <Link
                  href="/user/onboarding/create-us-account"
                  variant="noDecoration"
                  color="grey.900"
                  py="3"
                  px="5"
                >
                  Open US account
                </Link>
              </BasicMenuItem>
              <BasicMenuItem>
                <Link
                  href="/wallet"
                  variant="noDecoration"
                  color="grey.900"
                  py="3"
                  px="5"
                >
                  Create a Republic wallet
                </Link>
              </BasicMenuItem>
            </>
          )}
          {showAuthenticatedMenu && (
            <>
              <EditableEntities
                offerings={editableOfferings}
                teasers={teasers}
                mobile={true}
              />
              {/* All options minus logout */}
              {regularItems.map((item) => (
                <BasicMenuItem key={item?.href}>
                  {item?.icon ? (
                    <Link
                      py="3"
                      px="5"
                      onClick={() => {
                        trackNavigation(item.title, item.trackingParams);
                        handleItemClick(item.title);
                      }}
                      color="grey.900"
                      href={item.href}
                      variant="noDecoration"
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        {item.title}
                        <ListItemRightSide
                          title={item.title}
                          icon={item.icon}
                          notificationsCount={notificationsCount}
                          messagesCount={messagesCount}
                        />
                      </Flex>
                    </Link>
                  ) : (
                    <Link
                      href={item?.href}
                      onClick={() =>
                        trackNavigation(item?.title, item?.trackingParams)
                      }
                      color="grey.900"
                      variant="noDecoration"
                      py="3"
                      px="5"
                    >
                      {item?.title}
                    </Link>
                  )}
                </BasicMenuItem>
              ))}
            </>
          )}
        </BasicMenu>

        {hasCrossRegionAccountAccess && (
          <Box borderTop="1px solid" borderColor="grey.50" bg="white">
            <Text size="body-sm" color="grey.900" py="3" px="5">
              {hasAccountInUS
                ? 'You also have investment accounts in:'
                : 'You currently have an Investment account opened in:'}
            </Text>
            <BasicMenu>
              {hasAccountInEU && (
                <BasicMenuItem>
                  <Link
                    href="https://europe.republic.com"
                    variant="noDecoration"
                    color="grey.900"
                    py="3"
                    px="5"
                  >
                    European Union
                  </Link>
                </BasicMenuItem>
              )}
              {hasAccountInUK && (
                <BasicMenuItem>
                  <Link
                    href="https://europe.republic.com"
                    variant="noDecoration"
                    color="grey.900"
                    py="3"
                    px="5"
                  >
                    United Kingdom
                  </Link>
                </BasicMenuItem>
              )}
            </BasicMenu>
          </Box>
        )}
        <BasicMenu>
          {/* Logout option */}
          {logoutItem && (
            <BasicMenuItem>
              <Link
                href={logoutItem.href}
                variant="noDecoration"
                onClick={(e) => {
                  trackNavigation(logoutItem!.title);
                  handleLogout(e);
                }}
                color="grey.900"
                py="3"
                px="5"
              >
                {logoutItem.title}
              </Link>
            </BasicMenuItem>
          )}
        </BasicMenu>
      </Box>
    );
  }
);
